import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ColorParams } from '@bynder-studio/render-core';
import { defaultColor, Stroke } from '@bynder-studio/misc';
import useTextSelection from 'packages/hooks/useTextSelection';
import { activeColorPalettes } from 'packages/store/brandColors/brandColors.selectors';
import { collectColorsFromElement } from 'packages/pages/editor/RightSideMenu/Shots/Text/TextStyles/utils';
import { color2ColorParams } from 'packages/common/editor/editorHelper';
import useColorManipulatorCore from 'packages/pages/editor/RightSideMenu/Shots/Text/useColorManipulatorCore';

const useOutlineFontColorManipulator = ({ creativeModel, selectedElement }) => {
    const id = selectedElement.id;
    const { stroke: strokes, getRawStyles } = useTextSelection();
    const { palettes } = useSelector(activeColorPalettes);
    const elementColors = collectColorsFromElement(selectedElement, palettes, (run) => run.stroke?.strokeColor);

    const strokeColors = (strokes as Stroke[]).map((s) => s.strokeColor ?? defaultColor);

    const updateSingle = useCallback(
        (newStrokeColor: ColorParams) => {
            const [currentStroke] = strokes as Stroke[];
            const param = {
                updateTextSettingForSelection: {
                    settings: {
                        // type and weight are left out so each run keeps its own when they differ
                        stroke: {
                            punchOut: currentStroke.punchOut ?? false,
                            strokeColor: newStrokeColor,
                        },
                    },
                },
            };

            creativeModel.updateElement(id, param);
        },
        [creativeModel, id, strokes],
    );

    const updateEachRun = useCallback(
        (buildValue: (existingColor: ColorParams) => ColorParams) => {
            const styles = (getRawStyles().stroke as Stroke[]).map((s) => ({
                stroke: { ...s, punchOut: s.punchOut ?? false, strokeColor: buildValue(s.strokeColor ?? defaultColor) },
            }));
            const param = { updateTextSettingsByEachRun: { styles } };

            creativeModel.updateElement(id, param);
        },
        [creativeModel, id, getRawStyles],
    );

    const core = useColorManipulatorCore({
        colors: strokeColors,
        elementColors,
        palettes,
        updateSingle,
        updateEachRun,
    });

    const onChange = (val) => core.onChange(color2ColorParams(val));

    return { ...core, onChange };
};

export default useOutlineFontColorManipulator;
